import { useState, useRef, useEffect } from 'react';
import { useMultiTable } from '../contexts/MultiTableContext';

const PHASE_LABEL: Record<string, string> = {
  waiting:   'Waiting',
  countdown: 'Starting…',
  preflop:   'Pre-flop',
  flop:      'Flop',
  turn:      'Turn',
  river:     'River',
  showdown:  'Showdown',
};

const PHASE_DOT_CLASS: Record<string, string> = {
  waiting:   'bg-white/20',
  countdown: 'bg-amber-400',
  preflop:   'bg-emerald-400',
  flop:      'bg-emerald-400',
  turn:      'bg-emerald-400',
  river:     'bg-emerald-400',
  showdown:   'bg-violet-400',
};

const PHASE_TEXT_CLASS: Record<string, string> = {
  waiting:   'text-white/30',
  countdown: 'text-amber-400',
  preflop:   'text-emerald-400',
  flop:      'text-emerald-400',
  turn:      'text-emerald-400',
  river:     'text-emerald-400',
  showdown:  'text-violet-400',
};

export function TableDock() {
  const { tables, activeTableId, view, switchToTable, unregisterTable } = useMultiTable();

  // Only surface tables where the user is physically seated (myStack >= 0).
  // myStack is -1 when connected but not sitting at any seat.
  const seatedTables = Array.from(tables.values()).filter(
    (entry) => entry.snapshot.myStack >= 0,
  );

  if (seatedTables.length === 0) return null;

  return (
    <div
      className="shrink-0 border-t border-white/[0.07] bg-[#080810]/95 backdrop-blur-sm z-50"
      style={{ height: '72px' }}
    >
      <div className="flex items-center h-full px-3 gap-2 overflow-x-auto overflow-y-hidden">
        {/* Label */}
        <span className="shrink-0 text-white/20 text-[10px] font-semibold uppercase tracking-[0.15em] select-none mr-1">
          My Tables
        </span>

        {/* Table cards — only for tables the user is seated at */}
        {seatedTables.map((entry) => {
          const { snapshot } = entry;
          const isActive = view === 'table' && activeTableId === entry.id;
          const isInHand = ['preflop', 'flop', 'turn', 'river'].includes(snapshot.phase);

          return (
            <TableCard
              key={entry.id}
              tableId={entry.id}
              stakes={entry.config.stakes}
              phase={snapshot.phase}
              pot={snapshot.pot}
              playerCount={snapshot.playerCount}
              maxSeats={entry.config.maxSeats}
              isMyTurn={snapshot.isMyTurn}
              isInHand={isInHand}
              isActive={isActive}
              turnEndsAt={snapshot.turnEndsAt}
              holeCards={snapshot.holeCards}
              onSelect={() => switchToTable(entry.id)}
              onLeave={(e) => {
                e.stopPropagation();
                unregisterTable(entry.id);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ─── Individual table card ─── */

interface TableCardProps {
  tableId: string;
  stakes: string;
  phase: string;
  pot: number;
  playerCount: number;
  maxSeats: number;
  isMyTurn: boolean;
  isInHand: boolean;
  isActive: boolean;
  turnEndsAt: number;
  holeCards: string[];
  onSelect: () => void;
  onLeave: (e: React.MouseEvent) => void;
}

function TableCard({
  tableId,
  stakes,
  phase,
  pot,
  playerCount,
  maxSeats,
  isMyTurn,
  isInHand,
  isActive,
  turnEndsAt,
  onSelect,
  onLeave,
}: TableCardProps) {
  const [timeLeft, setTimeLeft] = useState(15);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live countdown only when it is our turn on this table
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!isMyTurn || turnEndsAt <= 0) {
      setTimeLeft(15);
      return;
    }
    const tick = () => {
      const secs = Math.ceil(Math.max(0, (turnEndsAt - Date.now()) / 1000));
      setTimeLeft(secs);
    };
    tick();
    intervalRef.current = setInterval(tick, 200);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isMyTurn, turnEndsAt]);

  const isUrgent = isMyTurn && timeLeft <= 5;
  const phaseLabel = PHASE_LABEL[phase] ?? phase;
  const dotClass = PHASE_DOT_CLASS[phase] ?? 'bg-white/20';
  const textClass = PHASE_TEXT_CLASS[phase] ?? 'text-white/30';

  // Card border/background depending on state
  const cardBase =
    'relative group shrink-0 flex flex-col justify-between rounded-xl cursor-pointer ' +
    'transition-all duration-150 select-none outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-primary/60 ';

  const cardVariant = isActive
    ? 'bg-white/[0.08] border border-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
    : isMyTurn
    ? 'bg-amber-500/[0.09] border border-amber-500/50 hover:bg-amber-500/[0.14]'
    : 'bg-white/[0.04] border border-white/[0.09] hover:bg-white/[0.07] hover:border-white/20';

  const urgentRing = isUrgent ? 'ring-2 ring-amber-500/70 animate-pulse' : '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
      aria-label={`${stakes} table — ${isMyTurn ? 'your turn' : phaseLabel}`}
      className={`${cardBase} ${cardVariant} ${urgentRing}`}
      style={{ width: '172px', height: '52px', padding: '8px 12px' }}
    >
      {/* ── Top row: stakes + turn indicator or phase ── */}
      <div className="flex items-center justify-between w-full gap-1">
        <span className="text-white/80 text-[11px] font-semibold leading-none truncate">
          {stakes} NLH
        </span>

        {isMyTurn ? (
          /* Turn badge */
          <span className="flex items-center gap-1 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping inline-block" />
            <span className="text-amber-400 text-[10px] font-bold leading-none tabular-nums">
              {timeLeft}s
            </span>
          </span>
        ) : (
          /* Phase indicator */
          <span className="flex items-center gap-1 shrink-0">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
            <span className={`text-[10px] leading-none ${textClass}`}>{phaseLabel}</span>
          </span>
        )}
      </div>

      {/* ── Bottom row: pot or seat count + active badge ── */}
      <div className="flex items-center justify-between w-full gap-1">
        <span className="text-white/35 text-[10px] leading-none tabular-nums">
          {isInHand
            ? `Pot $${(pot / 100).toFixed(2)}`
            : `${playerCount} / ${maxSeats} players`}
        </span>
        {isActive && (
          <span className="text-primary/60 text-[10px] leading-none font-medium shrink-0">
            Active
          </span>
        )}
      </div>

      {/* ── Leave × button — appears on hover ── */}
      <button
        type="button"
        onClick={onLeave}
        aria-label={`Leave table ${tableId}`}
        title="Leave table"
        className={
          'absolute -top-2 -right-2 w-5 h-5 rounded-full z-10 ' +
          'flex items-center justify-center ' +
          'bg-[#12121e] border border-white/[0.12] ' +
          'text-white/40 hover:text-white/90 hover:border-white/35 hover:bg-[#1e1e2e] ' +
          'opacity-0 group-hover:opacity-100 focus:opacity-100 ' +
          'transition-opacity duration-100 '
        }
      >
        <svg width="7" height="7" viewBox="0 0 7 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M1 1l5 5M6 1L1 6" />
        </svg>
      </button>
    </div>
  );
}
